import type { Adb } from "@yume-chan/adb";
import { FILE_PERMISSION } from "../constants";

/** Check if an error is an AbortError (from AbortController cancellation) */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Create a ReadableStream from a Uint8Array (used by ADB sync.write) */
export function uint8ArrayToReadableStream(
  data: Uint8Array,
  onProgress?: (bytesSent: number) => void,
): ReadableStream<Uint8Array> {
  if (!onProgress) {
    return new ReadableStream({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });
  }

  const CHUNK_SIZE = 256 * 1024;
  let offset = 0;
  return new ReadableStream({
    pull(controller) {
      if (offset >= data.byteLength) {
        controller.close();
        return;
      }
      const end = Math.min(offset + CHUNK_SIZE, data.byteLength);
      controller.enqueue(data.subarray(offset, end));
      offset = end;
      onProgress(offset);
    },
  });
}

/**
 * Download a file from URL with chunked progress tracking.
 * Returns the concatenated bytes and the total size (from content-length header).
 */
export async function openDownloadStream(
  url: string,
  signal: AbortSignal,
): Promise<{ stream: ReadableStream<Uint8Array>; totalSize: number }> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  if (!response.body) throw new Error("No response body");
  return {
    stream: response.body,
    totalSize: Number(response.headers.get("content-length") || 0),
  };
}

export function createProgressStream(
  totalSize: number,
  onProgress: (percent: number) => void,
): TransformStream<Uint8Array, Uint8Array> {
  let bytesRead = 0;
  let lastPercent = -1;
  return new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      bytesRead += chunk.byteLength;
      if (totalSize > 0) {
        const percent = Math.round((bytesRead / totalSize) * 100);
        if (percent !== lastPercent) {
          lastPercent = percent;
          onProgress(percent);
        }
      }
      controller.enqueue(chunk);
    },
  });
}

export async function downloadWithProgress(
  url: string,
  signal: AbortSignal,
  onProgress: (percent: number) => void,
): Promise<{ data: Uint8Array<ArrayBuffer>; totalSize: number }> {
  const { stream: body, totalSize } = await openDownloadStream(url, signal);

  const chunks: Uint8Array[] = [];
  const reader = body.getReader();
  let bytesRead = 0;
  let done = false;

  while (!done) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
    const result = await reader.read();
    done = result.done;
    if (!done && result.value) {
      chunks.push(result.value);
      bytesRead += result.value.byteLength;
      if (totalSize > 0) {
        onProgress(Math.round((bytesRead / totalSize) * 100));
      }
    }
  }

  const data = new Uint8Array(bytesRead);
  let offset = 0;
  for (const chunk of chunks) {
    data.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return { data, totalSize };
}

/**
 * Push a file to the device via ADB sync.
 * Handles sync lifecycle (create → write → dispose).
 */

export async function pushStreamToDevice(
  adb: Adb,
  path: string,
  stream: ReadableStream<Uint8Array>,
  permission = FILE_PERMISSION,
): Promise<void> {
  const sync = await adb.sync();
  try {
    const writeOptions = { filename: path, permission, file: stream };
    await sync.write(writeOptions);
  } finally {
    await sync.dispose();
  }
}

export async function pushFileToDevice(
  adb: Adb,
  path: string,
  data: Uint8Array,
  permission = FILE_PERMISSION,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const totalBytes = data.byteLength;
  const progressCb = onProgress
    ? (bytesSent: number) => onProgress(Math.round((bytesSent / totalBytes) * 100))
    : undefined;
  await pushStreamToDevice(adb, path, uint8ArrayToReadableStream(data, progressCb), permission);
}
